import * as React from 'react';
import { useState } from 'react';
import { Parallax } from 'react-scroll-parallax';
import { StaticImage } from 'gatsby-plugin-image';

import Layout from '../../components/Layout/index';
import HeroBanner from '../../components/HeroBanner';
import Button from '../../components/Button';
import CTABanner from '../../components/CTABanner';
import SplitContent from '../../components/SplitContent';
import Modal from '../../components/Modal';

import './style.scss';

const ChairAndCEOReportsPage = () => {
  const [chairModalOpen, setChairModalOpen] = useState(false);
  const [ceoModalOpen, setCeoModalOpen] = useState(false);

  return (
    <Layout pageTitle="Chair and CEO Reports">
      <HeroBanner
        title="Chair and Chief Executive Officer Reports"
        photo={
          <StaticImage
            className="shape shape-photo"
            src="./assets/hero-photo.png"
            layout="fixed" width={738}
            alt="John Vines OAM Professional Standards Councils Chair"
          />
        }
      >
        <p>
          This year the Councils have worked as a rigorous regulator promoting the offer of professional standards
          improvement and consumer protection, and strengthening the effectiveness and accessibility of the national
          regulatory system.
        </p>
        <Button color="primary" label="Read more about this chapter" icon="link" annualReportPage={12} />
      </HeroBanner>

      <section className="chair-report">
        <Parallax className="background" y={[-30, 30]}>
          <div className="bg1" />
        </Parallax>
        <div className="container">
          <SplitContent
            image={<StaticImage layout="fullWidth" className="shape shape-photo" src="./assets/chair-report.jpg" width={544} alt="Chair John Vines OAM at Professional Standards Councils meeting in Sydney" />}
          >
            <h2>Chair’s Report</h2>
            <p>
              “We continue to encourage and assist a broad range of occupational associations committed to developing
              their regulatory capacity and professional standards for their members that are responsive to consumer
              expectations.”
            </p>
            <p>
              <strong>
                John Vines OAM
                <br />
                Chair
                <br />
                Professional Standards Councils
              </strong>
            </p>
            <Button color="secondary" label="Open the letter" icon="plus" onClick={() => setChairModalOpen(true)} />
            <Modal isOpen={chairModalOpen} close={() => setChairModalOpen(false)}>
              <p>
                I am pleased to introduce this Annual Report on behalf of the Professional Standards Councils, outlining
                our work and achievements in 2020-21 and the outlook for 2021-22.
              </p>

              <p>
                At the outset, I would like to acknowledge how adeptly everyone in the national system for professional
                standards regulation has responded to the uncertain and challenging environment created by the COVID-19
                pandemic. It is a testament to the commitment, good will and resilience of all involved that we have not
                only continued the important work of regulating for consumer protection and advancing professional
                standards but improved upon it.
              </p>

              <p>
                The Councils prioritised our statutory function to enable professional standards schemes, continuing our
                focus in the built environment sector. As the sector faced diminishing public confidence in the
                industry&rsquo;s capacity to meet expected standards, we engaged with Building Ministers and
                occupational associations to help offer practical solutions. This work has involved us in advising
                occupational associations on the benefits of professional standards regulation and assisting them to
                develop risk management strategies and systems that can deliver consumer protection and continuous
                improvements in the competence and conduct of their members. We are now seeing applications for
                professional standards schemes come forward. We have approved two new schemes this reporting period. We
                continue to encourage and assist a broad range of occupational associations committed to developing
                their regulatory capacity and professional standards for their members that are responsive to consumer
                expectations.
              </p>

              <p>
                We also prioritised our work to ensure our scheme application processes are modern and easy to deal
                with. The application process is the gateway for occupational associations to join a community of
                professional standards regulators with direct access to the Councils&rsquo; help and resources. This
                positions them to develop their professional standards and regulatory systems to improve professional
                standards and better protect the consumers of their members&rsquo; services. Once approved, a scheme
                sets an association apart in its commitment and work to ensure its members are recognised for their
                competent and ethical conduct in providing services and ensuring avenues of resolution and redress are
                available to consumers. It is a significant undertaking. We have redesigned the process, through a
                collaborative effort with our stakeholders, to be less resource intensive for occupational associations
                and the Councils, so that efforts are directed to supporting better quality applications and scheme
                operations over their life. The pilot phase has started and will be a big focus in the year ahead.
              </p>

              <p>
                Additional priorities for us in the year ahead are focused on the final steps to achieving our goals
                published in Strategy 2021 - building the evidence base to demonstrate effectiveness, being enabled by
                digital service delivery, and delivering clear and practical encouragement and assistance. We will then
                take stock, engage with our stakeholders on their expectations, and set course for our next strategic
                horizon in 2022 and beyond. I would like to thank all of the Councils&rsquo; members who have served
                this year, for their excellent advice and regulatory stewardship. We farewelled Mr Terry Evans AM, who
                retired after more than 15 years exemplary service as a member of the Councils, and welcomed the
                appointments of Mr Richard Shields, Mr Tim Mellor and Ms Rebecca Fogerty. And I extend my appreciation
                to the whole team at the Professional Standards Authority, who have supported the work of the Councils
                with dedication and professionalism. In particular I would like to thank the Chief Executive Officer of
                the Professional Standards Authority, Roxane Marcelle-Shaw, for her great leadership of the Authority. I
                look forward to working together in the year ahead to assist occupational associations in their efforts
                to regulate their members for the benefit of consumers and communities across Australia.
              </p>

              <p>
                <strong>
                  John Vines OAM
                  <br />
                  Chair
                  <br />
                  Professional Standards Councils
                </strong>
              </p>
            </Modal>
          </SplitContent>
        </div>
      </section>

      <section className="ceo-report">
        <Parallax className="background" y={[-30, 30]}>
          <div className="bg2" />
        </Parallax>
        <div className="container">
          <SplitContent
            image={<StaticImage layout="fullWidth" className="shape shape-photo" src="./assets/ceo-report.jpg" width={544} alt="CEO Roxane Marcelle-Shaw at Professional Standards Councils meeting in Sydney" />}
            isReversed
          >
            <h2>Chief Executive Officer’s Report</h2>
            <p>
              “This Annual Report set outs our performance as a national system for professional standards regulation.
              It reflects the work of the Authority on behalf of the Councils, and the work of the occupational
              associations in their operation of schemes and implementation of risk management strategies.”
            </p>
            <p>
              <strong>
                Roxane Marcelle-Shaw
                <br />
                Chief Executive Officer
              </strong>
            </p>
            <Button color="secondary" label="Open the letter" icon="plus" onClick={() => setCeoModalOpen(true)} />
            <Modal isOpen={ceoModalOpen} close={() => setCeoModalOpen(false)}>
              <p>
                This Annual Report sets out our performance as a national system for professional standards regulation.
                It reflects the work of the Authority on behalf of the Councils, and the work of the occupational
                associations in their operation of schemes and implementation of risk management strategies. A defining
                feature of the year has been the adaptation by all of us in responding to the COVID-19 pandemic. I am
                pleased to say that we continued to work in ways that support the wellbeing of our staff and
                stakeholders, keeping connected and using new and innovative tools to help us achieve our shared
                objectives.
              </p>

              <p>
                Over the course of the year, we worked with 17 regulated associations to supervise the operation of
                their professional standards schemes and compliance with the professional standards legislation.
                Regulated associations expanded consumer protection with the number of association members subject to a
                professional standards scheme increasing to over 86,000 practitioners. Regulated associations also made
                significant improvements to their schemes and regulatory initiatives, as detailed in the review of
                regulatory operations that follows.
              </p>

              <p>
                We delivered new and revised regulatory guidance, worked with researchers to commence an evidence review
                of sexual harassment and risks of consumer harm in the legal profession, delivered two Professional
                Standards Forums online to share insights on codes of ethics and consumer expectations, and worked
                directly with regulated associations and stakeholders through workshops, consultations and reviews to
                contribute expertise on improving professional standards and consumer protection.
              </p>

              <p>
                As foreshadowed last reporting period, we stepped up our work across government and with occupational
                associations to assist consideration of professional standards regulation as a solution in responding to
                failures in ethical conduct or professional competence.
              </p>

              <p>
                In the year ahead, we will work closely with policy officers in the responsible departments in each
                state and territory and the Commonwealth to review our cost-allocation model under the Professional
                Standards Inter-Departmental Service Agreement 2016. This review is necessary to accommodate the full
                implementation of the mutual recognition provisions in the professional standards legislation. We will
                also work with policy officers to develop a proposal for a suite of important legislative amendments to
                improve harmonisation across the national system and support the achievement of the objects of the
                legislation.
              </p>

              <p>
                In all that we do and achieve, it is the people that make the difference. I am fortunate to serve a
                Chair, Deputy Chair and Councils members that offer outstanding leadership and wise counsel to guide our
                efforts and encourage our best. I am also fortunate to work with a team in the Authority that is expert,
                dedicated and supportive. It is evident in the large programme of work delivered by a small group of
                staff that they are smart and efficient in delivering excellent regulatory and operational services. My
                thanks to each of them for the many successes recorded in this Annual Report. I look forward to
                continuing our collective efforts to advance the national system for the regulation of professional
                standards.
              </p>

              <p>
                <strong>
                  Roxane Marcelle-Shaw
                  <br />
                  Chief Executive Officer
                  <br />
                  Professional Standards Authority
                </strong>
              </p>
            </Modal>
          </SplitContent>
        </div>
      </section>

      <CTABanner annualReportPage={12} />
    </Layout>
  );
};

export default ChairAndCEOReportsPage;
